import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { collection, doc, getDocs, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminApprovalsView.css';
import './StudentDetailPages.css';
import StatCards from './StatCards';
import { scopeForAdmin, schoolMatches } from '../utils/schoolScope';

function SuperAdminApprovalsView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || null }), [adminData, saSchoolId]);
  const [requests, setRequests] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeStatus = (status) => {
    if (!status) return 'pending';
    return status.toLowerCase();
  };

  const loadRequests = useCallback(async () => {
    console.log('🔥 AdminApprovals: Loading requests like iOS');
    try {
      const allRequests = [];
      const classesSnapshot = await getDocs(collection(db, 'classes'));
      const scopedClassDocs = scopeForAdmin(classesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })), adminData);
      console.log('🔥 AdminApprovals: Classes found', classesSnapshot.docs.length);

      for (const classDoc of scopedClassDocs) {
        const classId = classDoc.id;
        const className = classDoc.name || classId;
        const classSchoolId = classDoc.schoolId || null;

        // Load students in this class for name lookup
        const studentsSnapshot = await getDocs(collection(db, 'classes', classId, 'students'));
        const studentsById = {};
        studentsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          studentsById[doc.id] = data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || doc.id;
          // Also map by studentId if different
          if (data.studentId) {
            studentsById[data.studentId] = studentsById[doc.id];
          }
        });

        const assignmentsSnapshot = await getDocs(collection(db, 'classes', classId, 'assignments'));
        console.log('🔥 AdminApprovals: Assignments in class', classId, assignmentsSnapshot.docs.length);

        for (const assignmentDoc of assignmentsSnapshot.docs) {
          const assignmentId = assignmentDoc.id;
          const assignmentData = assignmentDoc.data();
          console.log('🔥 AdminApprovals: Assignment data', assignmentId, assignmentData);
          const assignmentName = assignmentData.title || assignmentId;
          const gradesSnapshot = await getDocs(collection(db, 'classes', classId, 'assignments', assignmentId, 'grades'));
          console.log('🔥 AdminApprovals: Grades in assignment', assignmentId, gradesSnapshot.docs.length);

          for (const gradeDoc of gradesSnapshot.docs) {
            const gradeId = gradeDoc.id;
            const gradeData = gradeDoc.data();
            const gradeStudentId = gradeData.studentId;
            const requestsSnapshot = await getDocs(collection(db, 'classes', classId, 'assignments', assignmentId, 'grades', gradeId, 'changeRequests'));
            console.log('🔥 AdminApprovals: Requests in grade', gradeId, requestsSnapshot.docs.length);

            requestsSnapshot.docs.forEach(doc => {
              const requestData = doc.data();
              const studentId = requestData.studentId || gradeStudentId;
              allRequests.push({
                id: doc.id,
                ref: doc.ref,
                ...requestData,
                classId,
                className,
                schoolId: classSchoolId,
                assignmentId,
                assignmentName,
                gradeId,
                gradeStudentId: studentId,
                studentName: studentsById[studentId] || studentId
              });
            });
          }
        }
      }

      console.log('🔥 AdminApprovals: Requests loaded', allRequests.length, allRequests);
      setRequests(allRequests);
    } catch (error) {
      console.error('🔥 AdminApprovals: Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const getFilteredRequests = () => {
    if (selectedFilter === 'all') return requests;
    return requests.filter(request => normalizeStatus(request.status) === selectedFilter);
  };

  const getPercentage = (score, maxScore) => {
    if (!maxScore || maxScore <= 0) return 0;
    return (score / maxScore) * 100;
  };

  const getLetterGrade = (percentage) => {
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  };

  const getStatusColor = (status) => {
    const normalized = normalizeStatus(status);
    const colors = {
      'pending': '#f59e0b',
      'approved': '#22c55e',
      'rejected': '#ef4444'
    };
    return colors[normalized] || '#666';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || '';

  const handleApprove = async (request) => {
    if (!confirm('Are you sure you want to approve this grade change?')) return;
    try {
      const now = new Date().toISOString();
      // Update the request status - use UUID for internal references like iOS
      await updateDoc(request.ref, {
        status: 'Approved',
        reviewedBy: adminData?.uuid || adminData?.uid, // Fallback to uid if uuid not available
        reviewedAt: now,
        reviewComment: ''
      });

      // Apply the grade change to the actual grade document (like iOS)
      if (request.classId && request.assignmentId && request.gradeId) {
        const gradeRef = doc(db, 'classes', request.classId, 'assignments', request.assignmentId, 'grades', request.gradeId);
        await updateDoc(gradeRef, {
          score: request.newScore,
          maxScore: request.newMaxScore,
          feedback: request.newFeedback || '',
          gradedBy: adminData?.uuid || adminData?.uid, // Fallback to uid if uuid not available
          gradedAt: now
        });
      }

      // Keep the top-level mirror (used for badge counts) in sync — merge so it's a no-op when absent
      if (request.id) {
        await setDoc(doc(db, 'gradeChangeRequests', request.id), { status: 'approved' }, { merge: true });
      }

      loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request');
    }
  };

  const handleReject = async (request) => {
    if (!confirm('Are you sure you want to reject this grade change?')) return;
    try {
      await updateDoc(request.ref, {
        status: 'Rejected',
        reviewedBy: adminData?.uuid || adminData?.uid, // Fallback to uid if uuid not available
        reviewedAt: new Date().toISOString(),
        reviewComment: ''
      });
      if (request.id) {
        await setDoc(doc(db, 'gradeChangeRequests', request.id), { status: 'rejected' }, { merge: true });
      }
      loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Failed to reject request');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredRequests = getFilteredRequests();
  const pendingCount = requests.filter(r => normalizeStatus(r.status) === 'pending').length;

  if (selectedRequest) {
    const originalPct = getPercentage(selectedRequest.originalScore, selectedRequest.originalMaxScore);
    const newPct = getPercentage(selectedRequest.newScore, selectedRequest.newMaxScore);
    const statusKey = normalizeStatus(selectedRequest.status);
    console.log('🔥 SuperAdminApprovals: Opened request detail', selectedRequest.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelectedRequest(null)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">✅</div>
          <div>
            <h2 className="detail-page-title">
              Grade Change Request
              <span className={`detail-badge ${statusKey}`}>{statusKey}</span>
            </h2>
            <p className="detail-page-subtitle">Requested {formatDate(selectedRequest.requestedAt)}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Request Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{schoolNameFor(selectedRequest.schoolId) || selectedRequest.schoolId || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Class</span><span className="detail-value">{selectedRequest.className || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Assignment</span><span className="detail-value">{selectedRequest.assignmentName || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Student</span><span className="detail-value">{selectedRequest.studentName || selectedRequest.studentId || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Original Score</span><span className="detail-value">{selectedRequest.originalScore}/{selectedRequest.originalMaxScore} ({originalPct.toFixed(1)}% {getLetterGrade(originalPct)})</span></div>
            <div className="detail-row"><span className="detail-label">New Score</span><span className="detail-value">{selectedRequest.newScore}/{selectedRequest.newMaxScore} ({newPct.toFixed(1)}% {getLetterGrade(newPct)})</span></div>
            <div className="detail-row"><span className="detail-label">Requested</span><span className="detail-value">{formatDate(selectedRequest.requestedAt)}</span></div>
            {selectedRequest.requestedByName && (
              <div className="detail-row"><span className="detail-label">Requested By</span><span className="detail-value">{selectedRequest.requestedByName}</span></div>
            )}
            {selectedRequest.reviewedAt && (
              <div className="detail-row"><span className="detail-label">Reviewed</span><span className="detail-value">{formatDate(selectedRequest.reviewedAt)}{selectedRequest.reviewedBy ? ` by ${selectedRequest.reviewedBy}` : ''}</span></div>
            )}
          </div>
        </div>

        <div className="detail-card">
          <h4>Reason</h4>
          <p className="detail-note">{selectedRequest.reason || 'No reason provided'}</p>
        </div>

        {statusKey === 'pending' && (
          <div className="detail-actions">
            <button className="btn-approve" onClick={() => { handleApprove(selectedRequest); setSelectedRequest(null); }}>Approve</button>
            <button className="btn-reject" onClick={() => { handleReject(selectedRequest); setSelectedRequest(null); }}>Reject</button>
          </div>
        )}
      </div>
    );
  }

  const apprStats = {
    p: requests.filter(r => normalizeStatus(r.status) === 'pending').length,
    a: requests.filter(r => normalizeStatus(r.status) === 'approved').length,
    r: requests.filter(r => normalizeStatus(r.status) === 'rejected').length,
    students: new Set(requests.map(r => r.studentId || r.studentName).filter(Boolean)).size,
    classes: new Set(requests.map(r => r.classId || r.className).filter(Boolean)).size
  };

  return (
    <div className="admin-approvals-view">
      <div className="view-header">
        <h2>Grade Approvals</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        {pendingCount > 0 && (
          <span className="pending-badge">{pendingCount} pending</span>
        )}
      </div>
      <StatCards items={[['Pending',apprStats.p],['Approved',apprStats.a],['Rejected',apprStats.r],['Total',requests.length],['Students',apprStats.students],['Classes',apprStats.classes]]} charts={[{title:'Approval Status',type:'pie',data:[{name:'Pending',value:apprStats.p,color:'#ea580c'},{name:'Approved',value:apprStats.a,color:'#16a34a'},{name:'Rejected',value:apprStats.r,color:'#dc2626'}]}]} />

      <div className="filter-bar">
        <button
          className={`filter-button ${selectedFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('pending')}
        >
          Pending ({requests.filter(r => normalizeStatus(r.status) === 'pending').length})
        </button>
        <button
          className={`filter-button ${selectedFilter === 'approved' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('approved')}
        >
          Approved ({requests.filter(r => normalizeStatus(r.status) === 'approved').length})
        </button>
        <button
          className={`filter-button ${selectedFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('rejected')}
        >
          Rejected ({requests.filter(r => normalizeStatus(r.status) === 'rejected').length})
        </button>
        <button
          className={`filter-button ${selectedFilter === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('all')}
        >
          All ({requests.length})
        </button>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h3>No Requests</h3>
          <p>No grade change requests found</p>
        </div>
      ) : (
        <div className="requests-list">
          {filteredRequests.map(request => {
            const originalPct = getPercentage(request.originalScore, request.originalMaxScore);
            const newPct = getPercentage(request.newScore, request.newMaxScore);
            const originalLetter = getLetterGrade(originalPct);
            const newLetter = getLetterGrade(newPct);
            return (
              <div key={request.id} className="request-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedRequest(request)}>
                <div className="request-info">
                  <h4>Grade Change Request</h4>
                  <p><strong>School:</strong> {schoolNameFor(request.schoolId) || request.schoolId || 'N/A'}</p>
                  <p><strong>Class:</strong> {request.className || 'N/A'}</p>
                  <p><strong>Assignment:</strong> {request.assignmentName || 'N/A'}</p>
                  <p><strong>Student:</strong> {request.studentName || request.studentId || 'N/A'}</p>
                  <p>
                    <strong>Score:</strong> {request.originalScore}/{request.originalMaxScore} ({originalPct.toFixed(1)}% {originalLetter})
                    {' → '}
                    {request.newScore}/{request.newMaxScore} ({newPct.toFixed(1)}% {newLetter})
                  </p>
                  <p><strong>Reason:</strong> {request.reason || 'No reason provided'}</p>
                  <p><strong>Requested:</strong> {formatDate(request.requestedAt)}</p>
                </div>
                <div className="request-status">
                  <span
                    className="status-badge"
                    style={{ background: `${getStatusColor(request.status)}20`, color: getStatusColor(request.status) }}
                  >
                    {normalizeStatus(request.status)}
                  </span>
                </div>
                {normalizeStatus(request.status) === 'pending' && (
                  <div className="request-actions">
                    <button
                      className="approve-button"
                      onClick={(e) => { e.stopPropagation(); handleApprove(request); }}
                    >
                      Approve
                    </button>
                    <button
                      className="reject-button"
                      onClick={(e) => { e.stopPropagation(); handleReject(request); }}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SuperAdminApprovalsView;
